import { STUCK_DELAY } from './gameConstants';
import type { ColorTheme } from './types/ColorTheme';
import type { VectorLike } from './types/VectorLike';
import { transformGuard } from './transformGuard';

export interface IMarblePhysics {
  createMarble(id: number, x: number, y: number): void;
  shakeMarble(id: number): void;
  removeMarble(id: number): void;
  getMarblePosition(id: number): { x: number; y: number; angle: number };
}

function lenSq(a: VectorLike, b: VectorLike): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

export class Marble {
  type = 'marble' as const;
  name: string = '';
  size = 0.5;
  color = 'red';
  hue = 0;
  weight = 1;
  isActive = false;

  private _stuckTime = 0;
  private lastPosition: VectorLike = { x: 0, y: 0 };
  private theme: ColorTheme | null = null;

  private physics: IMarblePhysics;
  id: number;

  get position(): { x: number; y: number; angle: number } {
    return this.physics.getMarblePosition(this.id) ?? { x: 0, y: 0, angle: 0 };
  }

  get x(): number {
    return this.position.x;
  }

  get y(): number {
    return this.position.y;
  }

  get angle(): number {
    return this.position.angle;
  }

  constructor(physics: IMarblePhysics, order: number, max: number, name?: string, weight = 1) {
    this.name = name ?? `M${order}`;
    this.weight = weight;
    this.physics = physics;

    const maxLine = Math.ceil(max / 10);
    const line = Math.floor(order / 10);
    const lineDelta = -Math.max(0, Math.ceil(maxLine - 5));
    this.hue = (360 / max) * order;
    this.color = `hsl(${this.hue} 100% 70%)`;
    this.id = order;

    physics.createMarble(order, 10.25 + (order % 10) * 0.6, maxLine - line + lineDelta);
  }

  update(deltaTime: number): void {
    if (this.isActive && lenSq(this.lastPosition, this.position) < 0.00001) {
      this._stuckTime += deltaTime;
      if (this._stuckTime > STUCK_DELAY) {
        this.physics.shakeMarble(this.id);
        this._stuckTime = 0;
      }
    } else {
      this._stuckTime = 0;
    }
    this.lastPosition = { x: this.position.x, y: this.position.y };
  }

  render(
    ctx: CanvasRenderingContext2D,
    zoom: number,
    outline: boolean,
    _isMinimap = false,
    _skin: CanvasImageSource | undefined,
    viewPort: { x: number; y: number; w: number; h: number; zoom: number },
    theme: ColorTheme
  ): void {
    this.theme = theme;
    const viewPortHw = viewPort.w / viewPort.zoom / 2;
    const viewPortHh = viewPort.h / viewPort.zoom / 2;
    const viewPortLeft = viewPort.x - viewPortHw;
    const viewPortRight = viewPort.x + viewPortHw;
    const viewPortTop = viewPort.y - viewPortHh - this.size / 2;
    const viewPortBottom = viewPort.y + viewPortHh;
    if (
      this.x < viewPortLeft ||
      this.x > viewPortRight ||
      this.y < viewPortTop ||
      this.y > viewPortBottom
    ) {
      return;
    }

    const transform = ctx.getTransform();
    this.renderNormal(ctx, zoom, outline);
    ctx.setTransform(transform);
  }

  private renderNormal(ctx: CanvasRenderingContext2D, zoom: number, outline: boolean): void {
    const hs = this.size / 2;
    const lightness = this.theme?.marbleLightness ?? 75;

    ctx.fillStyle = `hsl(${this.hue} 100% ${lightness}%)`;
    ctx.beginPath();
    ctx.arc(this.x, this.y, hs, 0, Math.PI * 2);
    ctx.fill();

    ctx.shadowColor = '';
    ctx.shadowBlur = 0;
    this.drawName(ctx, zoom);

    if (outline) {
      ctx.beginPath();
      ctx.strokeStyle = this.theme?.marbleWinningBorder ?? '#fff';
      ctx.lineWidth = 2 / zoom;
      ctx.arc(this.x, this.y, hs, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  private drawName(ctx: CanvasRenderingContext2D, zoom: number): void {
    transformGuard(ctx, () => {
      ctx.font = `12pt sans-serif`;
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.fillStyle = this.color;
      ctx.shadowBlur = 0;
      ctx.translate(this.x, this.y + 0.25);
      ctx.scale(1 / zoom, 1 / zoom);
      ctx.textAlign = 'center';
      ctx.strokeText(this.name, 0, 0);
      ctx.fillText(this.name, 0, 0);
    });
  }
}
